import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    AlertCircle,
    ArrowRightLeft,
    Banknote,
    CheckCircle,
    ClipboardCheck,
    Eye,
    Loader2,
    Minus,
    Plus,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { z } from 'zod';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { formatCurrency } from '~/lib/utils';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

// ============================================================================
// Types
// ============================================================================

type CashRegisterData = {
    id: string;
    name: string;
    isActive: boolean;
    requiresDailyCashStatement: boolean;
    storeId: number;
    createdAt: string;
    updatedAt: string;
    balanceEur: number;
};

// ============================================================================
// Constants
// ============================================================================

const WITHDRAWAL_REASONS = [
    { value: 'Einzahlung auf das Bankkonto', label: 'Einzahlung auf das Bankkonto' },
] as const;


// ============================================================================
// Schemas
// ============================================================================

const createCashRegisterSchema = z.object({
    name: z.string().min(1, 'Name ist erforderlich'),
});

const withdrawalSchema = z.object({
    amountEur: z.number().positive('Betrag muss positiv sein'),
    withdrawalReason: z.string().min(1, 'Grund ist erforderlich'),
    note: z.string().optional(),
});

const transferSchema = z.object({
    destinationCashRegisterId: z.string().min(1, 'Zielkasse ist erforderlich'),
    amountEur: z.number().positive('Betrag muss positiv sein'),
    note: z.string().optional(),
});

const depositSchema = z.object({
    amountEur: z.number().positive('Betrag muss positiv sein'),
    note: z.string().optional(),
});

// ============================================================================
// Sub-components: Dialogs
// ============================================================================

type CreateCashRegisterDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    storeId: number;
};

const CreateCashRegisterDialog = ({ open, onOpenChange, storeId }: CreateCashRegisterDialogProps) => {
    const trpcUtils = trpc.useUtils();

    const form = useForm<z.infer<typeof createCashRegisterSchema>>({
        resolver: zodResolver(createCashRegisterSchema),
        defaultValues: { name: '' },
    });

    useEffect(() => {
        if (open) {
            form.reset({ name: '' });
        }
    }, [open, form]);

    const createMutation = trpc.cashRegisters.create.useMutation({
        onSuccess: () => {
            toast.success('Kasse wurde erstellt');
            trpcUtils.cashRegisters.getByStore.invalidate({ storeId });
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleSubmit = form.handleSubmit((data) => {
        createMutation.mutate({ storeId, name: data.name });
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-[400px]'>
                <DialogHeader>
                    <DialogTitle>Neue Kasse erstellen</DialogTitle>
                    <DialogDescription>
                        Erstellen Sie eine neue Kasse für diesen Standort.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleSubmit} className='space-y-4'>
                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name der Kasse</FormLabel>
                                    <FormControl>
                                        <Input placeholder='z.B. Hauptkasse, Kasse Empfang...' {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Abbrechen
                            </Button>
                            <Button type='submit' disabled={createMutation.isPending}>
                                {createMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Erstellen
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

type WithdrawalDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cashRegister: CashRegisterData | null;
};

const WithdrawalDialog = ({ open, onOpenChange, cashRegister }: WithdrawalDialogProps) => {
    const trpcUtils = trpc.useUtils();

    const form = useForm<z.infer<typeof withdrawalSchema>>({
        resolver: zodResolver(withdrawalSchema),
        defaultValues: {
            amountEur: 0,
            withdrawalReason: '',
            note: '',
        },
    });

    useEffect(() => {
        if (open) {
            form.reset({ amountEur: 0, withdrawalReason: '', note: '' });
        }
    }, [open, form]);

    const { data: balanceData } = trpc.cashRegisters.getBalance.useQuery(
        { cashRegisterId: cashRegister?.id ?? '' },
        { enabled: open && !!cashRegister },
    );

    const currentBalance = balanceData?.balanceEur ?? cashRegister?.balanceEur ?? 0;
    const enteredAmount = form.watch('amountEur') || 0;
    const balanceAfter = currentBalance - enteredAmount;

    const withdrawalMutation = trpc.cashRegisters.createWithdrawal.useMutation({
        onSuccess: () => {
            toast.success('Entnahme wurde dokumentiert');
            if (cashRegister) {
                trpcUtils.cashRegisters.getByStore.invalidate({ storeId: cashRegister.storeId });
                trpcUtils.cashRegisters.getBalance.invalidate({ cashRegisterId: cashRegister.id });
                trpcUtils.cashRegisters.getTransactions.invalidate({ cashRegisterId: cashRegister.id });
            }
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleSubmit = form.handleSubmit((data) => {
        if (!cashRegister) {
            return;
        }
        withdrawalMutation.mutate({
            cashRegisterId: cashRegister.id,
            amountEur: data.amountEur,
            withdrawalReason: data.withdrawalReason,
            note: data.note,
        });
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-[450px]'>
                <DialogHeader>
                    <DialogTitle>Entnahme aus {cashRegister?.name}</DialogTitle>
                    <DialogDescription>
                        Dokumentieren Sie eine Geldentnahme aus dieser Kasse.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleSubmit} className='space-y-4'>
                        <FormField
                            control={form.control}
                            name='withdrawalReason'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Grund der Entnahme *</FormLabel>
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder='Grund auswählen' />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {WITHDRAWAL_REASONS.map((reason) => (
                                                <SelectItem key={reason.value} value={reason.value}>
                                                    {reason.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='amountEur'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Betrag (EUR)</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='number'
                                            step='0.01'
                                            min='0.01'
                                            placeholder='0.00'
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {enteredAmount > 0 && (
                            <div className='rounded-lg border p-3 space-y-2'>
                                {enteredAmount > currentBalance && (
                                    <p className='text-sm text-destructive font-medium'>
                                        Der Betrag darf den verfügbaren Kassenstand nicht übersteigen.
                                    </p>
                                )}
                                <div className='flex items-center justify-between text-sm'>
                                    <span className='text-muted-foreground'>Entnahmebetrag:</span>
                                    <span className='font-semibold text-destructive'>
                                        − {formatCurrency(enteredAmount)}
                                    </span>
                                </div>
                                <Separator />
                                <div className='flex items-center justify-between text-sm'>
                                    <span className='text-muted-foreground'>Aktueller Kassenstand:</span>
                                    <span className='font-medium'>{formatCurrency(currentBalance)}</span>
                                </div>
                                <div className='flex items-center justify-between text-sm'>
                                    <span className='text-muted-foreground'>Kassenstand danach:</span>
                                    <span className={`font-semibold ${balanceAfter < 0 ? 'text-destructive' : 'text-foreground'}`}>
                                        {formatCurrency(balanceAfter)}
                                    </span>
                                </div>
                            </div>
                        )}

                        <FormField
                            control={form.control}
                            name='note'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Notiz (optional)</FormLabel>
                                    <FormControl>
                                        <Textarea placeholder='Zusätzliche Informationen...' {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                type='submit'
                                variant='destructive'
                                disabled={
                                    withdrawalMutation.isPending ||
                                    enteredAmount <= 0 ||
                                    !form.watch('withdrawalReason') ||
                                    balanceAfter < 0
                                }
                            >
                                {withdrawalMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Entnahme dokumentieren
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

type TransferDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    sourceCashRegister: CashRegisterData | null;
    allCashRegisters: CashRegisterData[];
};

const TransferDialog = ({ open, onOpenChange, sourceCashRegister, allCashRegisters }: TransferDialogProps) => {
    const trpcUtils = trpc.useUtils();

    const form = useForm<z.infer<typeof transferSchema>>({
        resolver: zodResolver(transferSchema),
        defaultValues: {
            destinationCashRegisterId: '',
            amountEur: 0,
            note: '',
        },
    });

    useEffect(() => {
        if (open) {
            form.reset({ destinationCashRegisterId: '', amountEur: 0, note: '' });
        }
    }, [open, form]);

    const { data: sourceBalanceData } = trpc.cashRegisters.getBalance.useQuery(
        { cashRegisterId: sourceCashRegister?.id ?? '' },
        { enabled: open && !!sourceCashRegister },
    );

    const destinationId = form.watch('destinationCashRegisterId');
    const { data: destBalanceData } = trpc.cashRegisters.getBalance.useQuery(
        { cashRegisterId: destinationId },
        { enabled: open && !!destinationId },
    );

    const otherRegisters = useMemo(() =>
        allCashRegisters.filter((cr) => cr.id !== sourceCashRegister?.id && cr.isActive),
        [allCashRegisters, sourceCashRegister],
    );

    const sourceBalance = sourceBalanceData?.balanceEur ?? sourceCashRegister?.balanceEur ?? 0;
    const destinationRegister = allCashRegisters.find((cr) => cr.id === destinationId);
    const destBalance = destBalanceData?.balanceEur ?? destinationRegister?.balanceEur ?? 0;

    const enteredAmount = form.watch('amountEur') || 0;
    const sourceAfter = sourceBalance - enteredAmount;
    const destAfter = destBalance + enteredAmount;

    const transferMutation = trpc.cashRegisters.createTransfer.useMutation({
        onSuccess: () => {
            toast.success('Umbuchung wurde durchgeführt');
            if (sourceCashRegister) {
                trpcUtils.cashRegisters.getByStore.invalidate({ storeId: sourceCashRegister.storeId });
                trpcUtils.cashRegisters.getBalance.invalidate();
                trpcUtils.cashRegisters.getTransactions.invalidate();
            }
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleSubmit = form.handleSubmit((data) => {
        if (!sourceCashRegister) {
            return;
        }
        transferMutation.mutate({
            sourceCashRegisterId: sourceCashRegister.id,
            destinationCashRegisterId: data.destinationCashRegisterId,
            amountEur: data.amountEur,
            note: data.note,
        });
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-[500px]'>
                <DialogHeader>
                    <DialogTitle>Umbuchung von {sourceCashRegister?.name}</DialogTitle>
                    <DialogDescription>
                        Buchen Sie Geld von einer Kasse auf eine andere um.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleSubmit} className='space-y-4'>
                        <FormField
                            control={form.control}
                            name='destinationCashRegisterId'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Zielkasse *</FormLabel>
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder='Zielkasse auswählen' />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {otherRegisters.map((cr) => (
                                                <SelectItem key={cr.id} value={cr.id}>
                                                    {cr.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='amountEur'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Betrag (EUR)</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='number'
                                            step='0.01'
                                            min='0.01'
                                            placeholder='0.00'
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {enteredAmount > 0 && destinationId && (
                            <div className='rounded-lg border p-3 space-y-3'>
                                {enteredAmount > sourceBalance && (
                                    <p className='text-sm text-destructive font-medium'>
                                        Der Betrag darf den Kassenstand der Quellkasse nicht übersteigen.
                                    </p>
                                )}
                                <div className='space-y-1'>
                                    <p className='text-xs font-medium text-muted-foreground uppercase'>
                                        {sourceCashRegister?.name}
                                    </p>
                                    <div className='flex items-center justify-between text-sm'>
                                        <span className='text-muted-foreground'>Änderung:</span>
                                        <span className='font-semibold text-destructive'>
                                            − {formatCurrency(enteredAmount)}
                                        </span>
                                    </div>
                                    <div className='flex items-center justify-between text-sm'>
                                        <span className='text-muted-foreground'>
                                            {formatCurrency(sourceBalance)}
                                        </span>
                                        <span className='text-muted-foreground'>→</span>
                                        <span className={`font-semibold ${sourceAfter < 0 ? 'text-destructive' : 'text-foreground'}`}>
                                            {formatCurrency(sourceAfter)}
                                        </span>
                                    </div>
                                </div>
                                <Separator />
                                <div className='space-y-1'>
                                    <p className='text-xs font-medium text-muted-foreground uppercase'>
                                        {destinationRegister?.name}
                                    </p>
                                    <div className='flex items-center justify-between text-sm'>
                                        <span className='text-muted-foreground'>Änderung:</span>
                                        <span className='font-semibold text-green-600'>
                                            + {formatCurrency(enteredAmount)}
                                        </span>
                                    </div>
                                    <div className='flex items-center justify-between text-sm'>
                                        <span className='text-muted-foreground'>
                                            {formatCurrency(destBalance)}
                                        </span>
                                        <span className='text-muted-foreground'>→</span>
                                        <span className='font-semibold text-foreground'>
                                            {formatCurrency(destAfter)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <FormField
                            control={form.control}
                            name='note'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Notiz (optional)</FormLabel>
                                    <FormControl>
                                        <Textarea placeholder='Zusätzliche Informationen...' {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                type='submit'
                                disabled={
                                    transferMutation.isPending ||
                                    enteredAmount <= 0 ||
                                    !destinationId ||
                                    sourceAfter < 0
                                }
                            >
                                {transferMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Umbuchung durchführen
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

// ============================================================================
// Deposit Dialog
// ============================================================================

type DepositDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cashRegister: CashRegisterData | null;
};

const DepositDialog = ({ open, onOpenChange, cashRegister }: DepositDialogProps) => {
    const trpcUtils = trpc.useUtils();

    const form = useForm<z.infer<typeof depositSchema>>({
        resolver: zodResolver(depositSchema),
        defaultValues: {
            amountEur: 0,
            note: '',
        },
    });

    useEffect(() => {
        if (open) {
            form.reset({ amountEur: 0, note: '' });
        }
    }, [open, form]);

    const { data: balanceData } = trpc.cashRegisters.getBalance.useQuery(
        { cashRegisterId: cashRegister?.id ?? '' },
        { enabled: open && !!cashRegister },
    );

    const currentBalance = balanceData?.balanceEur ?? cashRegister?.balanceEur ?? 0;
    const enteredAmount = form.watch('amountEur') || 0;
    const balanceAfter = currentBalance + enteredAmount;

    const depositMutation = trpc.cashRegisters.createDeposit.useMutation({
        onSuccess: () => {
            toast.success('Einzahlung wurde dokumentiert');
            if (cashRegister) {
                trpcUtils.cashRegisters.getByStore.invalidate({ storeId: cashRegister.storeId });
                trpcUtils.cashRegisters.getBalance.invalidate({ cashRegisterId: cashRegister.id });
                trpcUtils.cashRegisters.getTransactions.invalidate({ cashRegisterId: cashRegister.id });
            }
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleSubmit = form.handleSubmit((data) => {
        if (!cashRegister) {
            return;
        }
        depositMutation.mutate({
            cashRegisterId: cashRegister.id,
            amountEur: data.amountEur,
            note: data.note,
        });
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-[450px]'>
                <DialogHeader>
                    <DialogTitle>Einzahlung in {cashRegister?.name}</DialogTitle>
                    <DialogDescription>
                        Dokumentieren Sie eine Einzahlung in diese Kasse.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleSubmit} className='space-y-4'>
                        <FormField
                            control={form.control}
                            name='amountEur'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Betrag (EUR)</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='number'
                                            step='0.01'
                                            min='0.01'
                                            placeholder='0.00'
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {enteredAmount > 0 && (
                            <div className='rounded-lg border p-3 space-y-2'>
                                <div className='flex items-center justify-between text-sm'>
                                    <span className='text-muted-foreground'>Einzahlungsbetrag:</span>
                                    <span className='font-semibold text-green-600'>
                                        + {formatCurrency(enteredAmount)}
                                    </span>
                                </div>
                                <Separator />
                                <div className='flex items-center justify-between text-sm'>
                                    <span className='text-muted-foreground'>Aktueller Kassenstand:</span>
                                    <span className='font-medium'>{formatCurrency(currentBalance)}</span>
                                </div>
                                <div className='flex items-center justify-between text-sm'>
                                    <span className='text-muted-foreground'>Kassenstand danach:</span>
                                    <span className='font-semibold'>{formatCurrency(balanceAfter)}</span>
                                </div>
                            </div>
                        )}

                        <FormField
                            control={form.control}
                            name='note'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Notiz (optional)</FormLabel>
                                    <FormControl>
                                        <Textarea placeholder='z.B. Anfangsbestand, Wechselgeld...' {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                type='submit'
                                disabled={depositMutation.isPending || enteredAmount <= 0}
                            >
                                {depositMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Einzahlung dokumentieren
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

// ============================================================================
// Main component
// ============================================================================

const STORAGE_KEY = 'cash-registers-selected-store';

const CashRegisters = () => {
    const navigate = useNavigate();
    const [selectedStoreId, setSelectedStoreId] = useState<string>(
        () => sessionStorage.getItem(STORAGE_KEY) ?? '',
    );
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [withdrawalRegister, setWithdrawalRegister] = useState<CashRegisterData | null>(null);
    const [transferRegister, setTransferRegister] = useState<CashRegisterData | null>(null);
    const [depositRegister, setDepositRegister] = useState<CashRegisterData | null>(null);

    const { role } = useUserStore(
        useShallow((state) => ({
            role: state.user?.role,
        })),
    );

    const isAdminOrOperator = role === UserRole.ADMIN || role === UserRole.OPERATOR;

    const { data: stores, isLoading: storesLoading } = trpc.cashRegisters.getStoresForUser.useQuery();

    useEffect(() => {
        if (!stores || stores.length === 0) return;
        const isValid = stores.some((s) => String(s.id) === selectedStoreId);
        if (!isValid) {
            setSelectedStoreId(String(stores[0].id));
        }
    }, [stores, selectedStoreId]);

    useEffect(() => {
        if (selectedStoreId) {
            sessionStorage.setItem(STORAGE_KEY, selectedStoreId);
        }
    }, [selectedStoreId]);

    const storeId = selectedStoreId ? parseInt(selectedStoreId, 10) : undefined;

    const { data: cashRegistersResponse, isLoading: registersLoading } = trpc.cashRegisters.getByStore.useQuery(
        { storeId: storeId!, includeInactive: isAdminOrOperator },
        { enabled: !!storeId },
    );

    const cashRegisters = cashRegistersResponse?.cashRegisters;

    const statementRegisterIds = useMemo(
        () => (cashRegisters ?? []).filter((cr) => cr.requiresDailyCashStatement).map((cr) => cr.id),
        [cashRegisters],
    );

    const { data: statementStatuses } = trpc.cashRegisters.getTodayStatementStatus.useQuery(
        { cashRegisterIds: statementRegisterIds },
        { enabled: statementRegisterIds.length > 0 },
    );

    const statementStatusMap = useMemo(() => {
        const map = new Map<string, { hasStatement: boolean; differenceEur: number; hasDifference: boolean }>();
        if (statementStatuses) {
            for (const s of statementStatuses) {
                map.set(s.cashRegisterId, s);
            }
        }
        return map;
    }, [statementStatuses]);

    return (
        <Content>
            <div className='space-y-6'>
                <div className='flex items-center justify-between'>
                    <div>
                        <h1 className='text-2xl font-bold'>Kassen</h1>
                        <p className='text-muted-foreground'>
                            Verwalten Sie die Kassen Ihrer Standorte.
                        </p>
                    </div>

                    {isAdminOrOperator && storeId && (
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className='mr-2 h-4 w-4' />
                            Neue Kasse
                        </Button>
                    )}
                </div>

                <div className='flex items-center gap-4'>
                    <div className='w-72'>
                        <Label className='mb-1.5 block text-sm font-medium'>Standort</Label>
                        {storesLoading ? (
                            <div className='h-10 rounded-md border bg-muted animate-pulse' />
                        ) : (
                            <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                                <SelectTrigger>
                                    <SelectValue placeholder='Standort auswählen' />
                                </SelectTrigger>
                                <SelectContent>
                                    {stores?.map((store) => (
                                        <SelectItem key={store.id} value={String(store.id)}>
                                            {store.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </div>

                {!storeId && (
                    <p className='text-muted-foreground text-center py-12'>
                        Bitte wählen Sie einen Standort aus.
                    </p>
                )}

                {storeId && registersLoading && (
                    <div className='flex items-center justify-center py-12'>
                        <Loader2 className='h-6 w-6 animate-spin' />
                    </div>
                )}

                {storeId && !registersLoading && cashRegisters && cashRegisters.length === 0 && (
                    <div className='text-center py-12'>
                        <Banknote className='mx-auto h-12 w-12 text-muted-foreground/50' />
                        <p className='mt-4 text-muted-foreground'>
                            Für diesen Standort sind noch keine Kassen eingerichtet.
                        </p>
                        {isAdminOrOperator && (
                            <Button className='mt-4' onClick={() => setShowCreateDialog(true)}>
                                <Plus className='mr-2 h-4 w-4' />
                                Erste Kasse erstellen
                            </Button>
                        )}
                    </div>
                )}

                {storeId && cashRegisters && cashRegisters.length > 0 && (
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        {cashRegisters.map((cr) => {
                            const hasOtherActiveRegisters = cashRegisters.some((r) => r.isActive && r.id !== cr.id);
                            const statementStatus = statementStatusMap.get(cr.id);

                            return (
                                <Card key={cr.id} className={!cr.isActive ? 'opacity-60' : ''}>
                                    <CardHeader className='pb-3'>
                                        <div className='flex items-center justify-between'>
                                            <div className='flex items-center gap-2'>
                                                <CardTitle className='text-base'>{cr.name}</CardTitle>
                                                {!cr.isActive && (
                                                    <Badge variant='secondary' className='text-xs'>Deaktiviert</Badge>
                                                )}
                                            </div>
                                            {cr.requiresDailyCashStatement && (() => {
                                                if (!statementStatus || !statementStatus.hasStatement) {
                                                    return (
                                                        <div className='flex items-center gap-1 text-yellow-600'>
                                                            <AlertCircle className='h-3.5 w-3.5' />
                                                            <span className='text-xs'>Kein Abschluss</span>
                                                        </div>
                                                    );
                                                }
                                                if (statementStatus.hasDifference) {
                                                    return (
                                                        <div className='flex items-center gap-1 text-destructive'>
                                                            <AlertCircle className='h-3.5 w-3.5' />
                                                            <span className='text-xs'>Differenz</span>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className='flex items-center gap-1 text-green-600'>
                                                        <CheckCircle className='h-3.5 w-3.5' />
                                                        <span className='text-xs'>OK</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </CardHeader>
                                    <CardContent className='space-y-3'>
                                        <div>
                                            <p className='text-sm text-muted-foreground'>Kassenstand</p>
                                            <p className={`text-xl font-bold font-mono ${cr.balanceEur < 0 ? 'text-destructive' : ''}`}>
                                                {formatCurrency(cr.balanceEur)}
                                            </p>
                                        </div>
                                        <div className='flex items-center gap-1.5 flex-wrap'>
                                            <TooltipProvider>
                                                {isAdminOrOperator && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant='outline'
                                                                size='icon'
                                                                className='h-8 w-8'
                                                                onClick={() => navigate(`/cash-register/${cr.id}`)}
                                                            >
                                                                <Eye className='h-3.5 w-3.5' />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Details & Transaktionen</TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {isAdminOrOperator && cr.isActive && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant='outline'
                                                                size='icon'
                                                                className='h-8 w-8'
                                                                onClick={() => setDepositRegister(cr)}
                                                            >
                                                                <Plus className='h-3.5 w-3.5' />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Einzahlung</TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {cr.isActive && (
                                                    <>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant='outline'
                                                                    size='icon'
                                                                    className='h-8 w-8'
                                                                    onClick={() => setWithdrawalRegister(cr)}
                                                                >
                                                                    <Minus className='h-3.5 w-3.5' />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Entnahme</TooltipContent>
                                                        </Tooltip>
                                                        {hasOtherActiveRegisters && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant='outline'
                                                                        size='icon'
                                                                        className='h-8 w-8'
                                                                        onClick={() => setTransferRegister(cr)}
                                                                    >
                                                                        <ArrowRightLeft className='h-3.5 w-3.5' />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Umbuchung</TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                        {cr.requiresDailyCashStatement && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant='outline'
                                                                        size='icon'
                                                                        className='h-8 w-8'
                                                                        onClick={() => navigate(`/cash-register/${cr.id}/statement`)}
                                                                    >
                                                                        <ClipboardCheck className='h-3.5 w-3.5' />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Kassensturz</TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </TooltipProvider>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {storeId && (
                <CreateCashRegisterDialog
                    open={showCreateDialog}
                    onOpenChange={setShowCreateDialog}
                    storeId={storeId}
                />
            )}

            <WithdrawalDialog
                open={!!withdrawalRegister}
                onOpenChange={(open) => { if (!open) { setWithdrawalRegister(null); } }}
                cashRegister={withdrawalRegister}
            />

            <TransferDialog
                open={!!transferRegister}
                onOpenChange={(open) => { if (!open) { setTransferRegister(null); } }}
                sourceCashRegister={transferRegister}
                allCashRegisters={cashRegisters ?? []}
            />

            <DepositDialog
                open={!!depositRegister}
                onOpenChange={(open) => { if (!open) { setDepositRegister(null); } }}
                cashRegister={depositRegister}
            />
        </Content>
    );
};

export default CashRegisters;
